import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

// ---------------------------------------------------------------------------
// Toggle
//
// Pill-shaped on/off switch with a sliding thumb. Used for binary preferences
// where a native checkbox would feel out of place (the show-archived filter
// on the Builds page is the first consumer). Renders a real `<button
// role="switch">` so screen readers and keyboards interact with it the way
// they expect.
// ---------------------------------------------------------------------------

export type ToggleProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'type' | 'role' | 'aria-checked'
> & {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  // Accessible label describing what the toggle controls. Required because
  // the toggle has no visible label of its own.
  'aria-label': string;
};

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ checked, onCheckedChange, className, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked
          ? 'border-accent bg-accent-bg'
          : 'border-border-strong bg-surface-2',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none block h-3.5 w-3.5 rounded-full transition-transform',
          checked
            ? 'translate-x-[1.125rem] bg-accent'
            : 'translate-x-[0.125rem] bg-text-muted',
        )}
      />
    </button>
  ),
);
Toggle.displayName = 'Toggle';
